// src/components/dashboard/TopVehiclesTable.js
import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Top Vehicles Table
 * Shows the most booked vehicles with performance metrics
 *
 * @param {Array} data - Array of vehicle data with { vehicle, bookings, completed, noShows, completionRate, noShowRate }
 */
const TopVehiclesTable = ({ data }) => {
  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border border-app-border-light dark:border-app-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4">
        <h3 className="text-base lg:text-lg font-semibold text-app-text-light dark:text-app-text">
          Top Vehicles by Bookings
        </h3>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
          Most popular test drive vehicles
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-app-border-light dark:border-app-border">
              <th className="text-left py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold">
                Rank
              </th>
              <th className="text-left py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold">
                Vehicle
              </th>
              <th className="text-center py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold">
                Bookings
              </th>
              <th className="text-center py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold">
                Completed
              </th>
              <th className="text-center py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold hidden lg:table-cell">
                No Shows
              </th>
              <th className="text-center py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold">
                Completion Rate
              </th>
              <th className="text-center py-3 px-2 text-app-text-secondary-light dark:text-app-text-secondary font-semibold hidden lg:table-cell">
                No Show Rate
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((vehicle, index) => (
              <tr
                key={index}
                className="border-b border-app-border-light/50 dark:border-app-border/50 hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary/30 transition-colors"
              >
                {/* Rank */}
                <td className="py-3 px-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-800 text-app-text-light dark:text-app-text font-semibold">
                    {index + 1}
                  </div>
                </td>

                {/* Vehicle */}
                <td className="py-3 px-2">
                  <div className="font-semibold text-app-text-light dark:text-app-text">
                    {vehicle.vehicle}
                  </div>
                </td>

                {/* Bookings */}
                <td className="py-3 px-2 text-center">
                  <div className="font-semibold text-blue-600 dark:text-blue-400">
                    {vehicle.bookings}
                  </div>
                </td>

                {/* Completed */}
                <td className="py-3 px-2 text-center">
                  <div className="font-semibold text-green-600 dark:text-green-400">
                    {vehicle.completed}
                  </div>
                </td>

                {/* No Shows - hidden on mobile */}
                <td className="py-3 px-2 text-center hidden lg:table-cell">
                  <div className="font-semibold text-orange-600 dark:text-orange-400">
                    {vehicle.noShows}
                  </div>
                </td>

                {/* Completion Rate */}
                <td className="py-3 px-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {vehicle.completionRate >= 80 ? (
                      <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                    ) : vehicle.completionRate < 70 ? (
                      <TrendingDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                    ) : null}
                    <span className={`font-semibold ${
                      vehicle.completionRate >= 80
                        ? 'text-green-600 dark:text-green-400'
                        : vehicle.completionRate >= 70
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {vehicle.completionRate}%
                    </span>
                  </div>
                </td>

                {/* No Show Rate - hidden on mobile */}
                <td className="py-3 px-2 text-center hidden lg:table-cell">
                  <span className={`font-semibold ${
                    vehicle.noShowRate < 10
                      ? 'text-green-600 dark:text-green-400'
                      : vehicle.noShowRate < 15
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {vehicle.noShowRate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Performance indicators legend */}
      <div className="mt-4 pt-4 border-t border-app-border-light dark:border-app-border">
        <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mb-2">Performance Indicators:</div>
        <div className="flex flex-wrap gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400"></div>
            <span className="text-app-text-secondary-light dark:text-app-text-secondary">Excellent (≥80%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500 dark:bg-yellow-400"></div>
            <span className="text-app-text-secondary-light dark:text-app-text-secondary">Good (70-79%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500 dark:bg-red-400"></div>
            <span className="text-app-text-secondary-light dark:text-app-text-secondary">Needs Improvement (&lt;70%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopVehiclesTable;
